/**
 * Publish Service - Handles publishing configuration changes and triggering reprocessing
 * 
 * This service manages the publish workflow for all configuration changes including:
 * - Field mapping changes
 * - Grouping/segmentation changes
 * - Listing rule changes
 * 
 * When ANY config is published, it triggers a full reprocess of all vehicles
 * with the updated configuration.
 */

/**
 * Publishes all configuration changes and triggers reprocessing of all vehicles.
 * 
 * Uses the new /publish-changes orchestration endpoint that:
 * 1. Snapshots ConfigDraft to derive configChanges
 * 2. Promotes config drafts to JAMS
 * 3. Publishes vehicle drafts
 * 4. Triggers import feed with forceReprocessAll + configChanges metadata
 * 
 * @param endpoint - ISE backend endpoint
 * @param accountId - Dealer account ID (jazelId)
 * @param sessionToken - Auth token
 * @returns structured result with backend validation details when publish is rejected
 */
export type PublishAllChangesResult = {
  ok: boolean
  status?: number
  error?: string
  code?: string
  message?: string
  messages?: string[]
  details?: any
}

export const publishAllChanges = async (
  endpoint: string,
  accountId: string,
  sessionToken: string,
): Promise<PublishAllChangesResult> => {
  const publishChangesUrl = `${endpoint}/publish-changes`
  console.log('Publishing changes (orchestrated)...', publishChangesUrl)
  
  const response = await fetch(publishChangesUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({
      jazelId: accountId,
    }),
  })

  console.log('publishAllChanges response', response)

  if (!response.ok) {
    let details: any = null
    try {
      details = await response.json()
    } catch {}
    console.error(
      'Error publishing changes.',
      details?.message || details?.error || `HTTP ${response.status} ${response.statusText}`,
      details?.hint ? `Hint: ${details.hint}` : '',
    )
    return {
      ok: false,
      status: response.status,
      error: details?.error,
      code: details?.code,
      message: details?.message || details?.error || `HTTP ${response.status} ${response.statusText}`,
      messages: Array.isArray(details?.messages) ? details.messages : undefined,
      details: details?.details,
    }
  }

  console.log('✅ Publish changes accepted (processing in background)')
  return { ok: true, status: response.status }
}

/**
 * Republish currently published inventory using currently published configuration.
 * Never falls back to /publish-changes.
 */
export const republishCurrentInventory = async (
  endpoint: string,
  accountId: string,
  sessionToken: string,
  reason: string,
): Promise<PublishAllChangesResult> => {
  const republishUrl = `${endpoint}/republish-current-inventory`
  console.log('Republishing current inventory...', republishUrl)

  const response = await fetch(republishUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({
      jazelId: accountId,
      reason: reason.trim(),
    }),
  })

  console.log('republishCurrentInventory response', response)

  if (!response.ok) {
    let details: any = null
    try {
      details = await response.json()
    } catch {}
    console.error(
      'Error republishing current inventory.',
      details?.message || details?.error || `HTTP ${response.status} ${response.statusText}`,
    )
    return {
      ok: false,
      status: response.status,
      error: details?.error,
      code: details?.code,
      message: details?.message || details?.error || `HTTP ${response.status} ${response.statusText}`,
      messages: Array.isArray(details?.messages) ? details.messages : undefined,
      details: details?.details,
    }
  }

  console.log('✅ Republish current inventory accepted (processing in background)')
  return { ok: true, status: response.status }
}

/**
 * Checks the status of the most recent publish/reprocess job
 * 
 * @param endpoint - ISE backend endpoint
 * @param accountId - Dealer account ID (jazelId)
 * @returns Promise<string> - Status of the most recent job (INITIATED, PROCESSING, SUCCEEDED, FAILED, or empty string)
 */
export const checkPublishStatus = async (
  endpoint: string,
  accountId: string,
): Promise<string> => {
  const path = `check-import-feed-status`
  const response = await fetch(`${endpoint}/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      jazelId: accountId,
    }),
  })

  console.log('checkPublishStatus response', response)

  if (!response.ok) {
    console.error(
      'Error checking publish status. Please check the endpoint and your permissions.',
    )
    return ''
  }

  // New response shape: { jobs: [...], count, jazelId, message }
  const data = await response.json()
  const jobs = Array.isArray(data?.jobs) ? data.jobs : []
  if (jobs.length === 0) return ''
  jobs.sort(
    (a: any, b: any) =>
      new Date(b?.updatedAt || b?.createdAt || 0).getTime() -
      new Date(a?.updatedAt || a?.createdAt || 0).getTime(),
  )
  const latest = jobs[0] || {}
  return latest.status ?? ''
}

/**
 * Checks if a reprocess pipeline is currently running
 * 
 * @param endpoint - ISE backend endpoint
 * @param accountId - Dealer account ID (jazelId)
 * @returns Promise<object | null> - Object with isRunning, status, and statusText, or null if error
 */
export const checkPipelineStatus = async (
  endpoint: string,
  accountId: string,
): Promise<{ isRunning: boolean; status: string; statusText: string } | null> => {
  const path = `check-import-feed-status`
  try {
    const response = await fetch(`${endpoint}/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jazelId: accountId,
        // Fetch a small recent window; we select the latest job for the global
        // banner (see below). The window also lets the status endpoint reconcile
        // any stale non-terminal jobs it returns.
        limit: 25,
      }),
    })

    if (!response.ok) {
      console.error('Error checking pipeline status.')
      return null
    }

    // Expected response:
    // { jobs: [{ status, updatedAt, ... }], count, message, jazelId }
    const data = await response.json()
    const jobs = Array.isArray(data?.jobs) ? data.jobs : []
    if (jobs.length === 0) {
      return { isRunning: false, status: '', statusText: '' }
    }

    jobs.sort(
      (a: any, b: any) =>
        new Date(b?.updatedAt || b?.createdAt || 0).getTime() -
        new Date(a?.updatedAt || a?.createdAt || 0).getTime(),
    )

    // Per ISE import feed job tracking, valid statuses are:
    // INITIATED | PROCESSING | SUCCEEDED | FAILED | SKIPPED
    //
    // Defensive safeguard: this path runs only when NO publish lock is held (the
    // lock-held branch in App.vue follows the lock owner's correlationId). With no
    // active publish, an older non-terminal job cannot still be running, so we use
    // the LATEST job only. Previously we scanned for any INITIATED/PROCESSING job
    // across the recent list, which let a single orphaned job (e.g. a startup
    // failure that never terminalized) keep the global banner alive forever even
    // though newer jobs had succeeded.
    const runningStatuses = ['INITIATED', 'PROCESSING']
    const latest = jobs[0] || null
    const status = String(latest?.status || '').toUpperCase()
    const isRunning = runningStatuses.includes(status)

    return {
      isRunning,
      status,
      statusText: status,
    }
  } catch (error) {
    console.error('Error checking pipeline status:', error)
    return null
  }
}

/**
 * Fetch a specific import feed job by correlationId.
 * Used when a publish lock is held so the UI can follow the lock owner job, not "latest by jazelId".
 */
export const checkImportFeedJobByCorrelationId = async (
  endpoint: string,
  correlationId: string,
): Promise<{
  status: string
  triggerType?: string
  requestedByUsername?: string
  feed?: string
  fileKey?: string
  startedAt?: string
  finishedAt?: string
  errorMessage?: string
  result?: any | null
} | null> => {
  const path = `check-import-feed-status`
  const cid = String(correlationId || '').trim()
  if (!cid) return null

  try {
    const response = await fetch(`${endpoint}/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        correlationId: cid,
      }),
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json().catch(() => null)
    const job = data?.job ?? null
    const status = String(job?.status || '').toUpperCase()
    const fileKey = String(job?.fileKey || job?.result?.fileKey || '').trim()
    return {
      status,
      triggerType: job?.triggerType ? String(job.triggerType) : undefined,
      requestedByUsername: job?.requestedByUsername ? String(job.requestedByUsername) : undefined,
      feed: job?.feed ? String(job.feed) : undefined,
      fileKey: fileKey || undefined,
      startedAt: job?.startedAt ? String(job.startedAt) : undefined,
      finishedAt: job?.finishedAt ? String(job.finishedAt) : undefined,
      errorMessage: job?.errorMessage ? String(job.errorMessage) : undefined,
      result: job?.result ?? null,
    }
  } catch {
    return null
  }
}

export type UnpublishedChangesSummary = {
  jazelId: string
  hasUnpublished: boolean
  counts: {
    vehicleDrafts: number
    inventoryDrafts: number
  }
  draftVehicleVins?: string[]
  configDrafts: {
    fieldMapping: { exists: boolean; savedAt?: string; savedBy?: string }
    segments: { exists: boolean; savedAt?: string; savedBy?: string }
    inventoryMembership: { exists: boolean; savedAt?: string; savedBy?: string }
    iseSettings: { exists: boolean; savedAt?: string; savedBy?: string }
    groupSiteConfig: { exists: boolean; savedAt?: string; savedBy?: string }
    feedFieldRules: { exists: boolean; savedAt?: string; savedBy?: string }
  }
  sourcesChecked: Array<'VehicleDraft' | 'CurrentInventoryDrafts' | 'ConfigDraft'>
}/**
 * Fetches authoritative unpublished changes summary from backend.
 * Checks ConfigDraft, VehicleDraft, and CurrentInventory.publishState='draft'.
 * 
 * @param endpoint - ISE backend endpoint
 * @param jazelId - Dealer account ID
 * @param sessionToken - Bearer token for auth
 * @returns Promise<UnpublishedChangesSummary>
 */
export const fetchUnpublishedChangesSummary = async (
  endpoint: string,
  jazelId: string,
  sessionToken: string,
): Promise<UnpublishedChangesSummary> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = new URL(`${base}/unpublished-changes/summary`)
  url.searchParams.set('jazelId', jazelId)

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(`Failed to load unpublished changes summary: ${text || response.statusText}`)
  }

  return response.json()
}

/**
 * Check if a publish lock is currently active for a dealer.
 * Used by the UI to disable the Publish Changes button when another publish is in progress.
 * 
 * @param endpoint - ISE backend endpoint
 * @param jazelId - Dealer account ID
 * @returns Promise with lock status
 */
export const checkPublishLockStatus = async (
  endpoint: string,
  jazelId: string,
): Promise<{
  locked: boolean
  ownerCorrelationId?: string
  expiresAtEpoch?: number
  expiresAt?: string
}> => {
  const base = endpoint.replace(/\/+$/g, '')
  const url = `${base}/publish-lock-status?jazelId=${encodeURIComponent(jazelId)}`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    console.error('Failed to check publish lock status:', response.statusText)
    return { locked: false }
  }

  const data = await response.json()
  return {
    locked: data.locked ?? false,
    ownerCorrelationId: data.ownerCorrelationId,
    expiresAtEpoch: data.expiresAtEpoch,
    expiresAt: data.expiresAt,
  }
}
